import React, { useEffect, useState } from "react";
import { ArrowLeft, Film, Heart, Trash2, Tv } from "lucide-react";
import "./library.css";

// ───────────────────────── helpers ─────────────────────────

export const isSeries = (type) => /series|tv|show/i.test(type || "");

export const splitGenres = (genre) =>
  (genre || "")
    .split(/[,/]/)
    .map((g) => g.trim())
    .filter(Boolean);

const isUsablePoster = (url) =>
  !!url && !url.includes("xxx.jpg") && !url.includes("placeholder");

// Posters saved from the Liked-page search used TMDB's tiny w200 size.
// Ask TMDB for a sharper one when we draw them large.
export const sharpPoster = (url) =>
  url ? url.replace("/t/p/w200/", "/t/p/w500/") : url;

export const FILTERS = [
  { id: "all", label: "All" },
  { id: "movie", label: "Movies" },
  { id: "series", label: "Series" },
];

export const applyTypeFilter = (items, filter) => {
  if (filter === "movie") return items.filter((i) => !isSeries(i.type));
  if (filter === "series") return items.filter((i) => isSeries(i.type));
  return items;
};

// ───────────────────────── page shell ─────────────────────────

export function LibraryPage({
  title,
  icon,
  subtitle,
  onBack,
  toolbar,
  toast,
  overlay,
  children,
}) {
  return (
    <div className="nq-lib">
      <div className="nq-lib__wrap">
        <button type="button" className="nq-btn nq-btn--ghost" onClick={onBack}>
          <ArrowLeft size={18} aria-hidden="true" />
          Back to home
        </button>

        <header className="nq-lib__header">
          <h1 className="nq-lib__title">
            {icon}
            {title}
          </h1>
          {subtitle ? <p className="nq-lib__subtitle">{subtitle}</p> : null}
        </header>

        {toolbar ? <div className="nq-lib__toolbar">{toolbar}</div> : null}

        <main>{children}</main>
      </div>

      {toast ? (
        <div
          className={`nq-toast nq-toast--${toast.type || "success"}`}
          role="status"
          aria-live="polite"
        >
          {toast.message}
        </div>
      ) : null}

      {overlay}
    </div>
  );
}

// ───────────────────────── small pieces ─────────────────────────

export function FilterTabs({ value, onChange }) {
  return (
    <div className="nq-tabs" role="group" aria-label="Filter by type">
      {FILTERS.map((f) => (
        <button
          key={f.id}
          type="button"
          className="nq-chip-btn"
          aria-pressed={value === f.id}
          onClick={() => onChange(f.id)}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

export function EmptyState({ icon, title, text, actionLabel, onAction }) {
  return (
    <div className="nq-empty">
      <div className="nq-empty__icon" aria-hidden="true">
        {icon}
      </div>
      <h2 className="nq-empty__title">{title}</h2>
      <p className="nq-empty__text">{text}</p>
      {actionLabel ? (
        <button
          type="button"
          className="nq-btn nq-btn--primary"
          onClick={onAction}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

export function Poster({ url, type, variant = "thumb" }) {
  const [failed, setFailed] = useState(false);
  const ok = isUsablePoster(url) && !failed;
  return (
    <div className={`nq-poster nq-poster--${variant}`}>
      {ok ? (
        // alt is empty on purpose: the title is always printed right next to it
        <img
          src={sharpPoster(url)}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : isSeries(type) ? (
        <Tv size={variant === "full" ? 40 : 24} aria-hidden="true" />
      ) : (
        <Film size={variant === "full" ? 40 : 24} aria-hidden="true" />
      )}
    </div>
  );
}

export function Chips({ items, platform = false }) {
  if (!items || items.length === 0) return null;
  return (
    <ul className="nq-chips">
      {items.map((c) => (
        <li
          key={c}
          className={`nq-chip${platform ? " nq-chip--platform" : ""}`}
        >
          {c}
        </li>
      ))}
    </ul>
  );
}

export function LikeButton({ liked, onClick, disabled }) {
  return (
    <button
      type="button"
      className={`nq-btn nq-btn--ghost${liked ? " is-liked" : ""}`}
      aria-pressed={liked}
      disabled={disabled}
      onClick={onClick}
      title={liked ? "Remove from liked" : "Add to liked"}
    >
      <Heart
        size={16}
        fill={liked ? "currentColor" : "none"}
        aria-hidden="true"
      />
      {liked ? "Liked" : "Like"}
    </button>
  );
}

// First click asks "Remove?", second click confirms. Resets itself after 4s.
export function ConfirmRemove({ label, onConfirm }) {
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    if (!asking) return undefined;
    const t = setTimeout(() => setAsking(false), 4000);
    return () => clearTimeout(t);
  }, [asking]);

  if (!asking) {
    return (
      <button
        type="button"
        className="nq-icon-btn"
        aria-label={`Remove ${label}`}
        title="Remove"
        onClick={() => setAsking(true)}
      >
        <Trash2 size={16} aria-hidden="true" />
      </button>
    );
  }

  return (
    <span
      className="nq-confirm"
      role="group"
      aria-label={`Confirm removing ${label}`}
    >
      <button
        type="button"
        className="nq-btn nq-btn--danger"
        onClick={onConfirm}
        autoFocus
      >
        Remove
      </button>
      <button
        type="button"
        className="nq-btn nq-btn--ghost"
        onClick={() => setAsking(false)}
      >
        Keep
      </button>
    </span>
  );
}
